import { Checkbox } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';
import type React from 'react';
import { useCallback, useMemo } from 'react';

import { checkArraySubset } from '../../utils';
import { CheckboxWithToolTip } from '../WithTooltip';

export interface IAgCheckBoxCellRendererComponent extends ICellRendererParams {
    checkedIds?: number[];
    dependentIds?: number[];
    message?: string;
    isDisabled?: boolean;
    onCheckChange: (checkedIds: number[]) => void;
    idKey?: string;
    checkboxPropsGetter?: (id: any) => { checked: boolean; intermediate: boolean; disabled: boolean };
    noCheckboxKey?: string;
    noCheckboxChecker?: (data: any) => boolean;
}

const style = { display: 'flex', alignItems: 'center', height: '100%' };

export const AgCheckBoxCellRendererComponent: React.FC<IAgCheckBoxCellRendererComponent> = (props) => {
    const {
        checkedIds,
        checkboxPropsGetter,
        dependentIds,
        message,
        isDisabled,
        onCheckChange,
        data,
        idKey = 'id',
        noCheckboxKey,
        noCheckboxChecker,
    } = props;

    const checkboxProps = useMemo(
        () =>
            checkboxPropsGetter
                ? checkboxPropsGetter(data?.[idKey])
                : {
                      checked:
                          (checkedIds?.length && dependentIds?.length && checkArraySubset(checkedIds, dependentIds)) ||
                          checkedIds?.includes(data?.[idKey]),
                      intermediate: !!(
                          checkedIds?.length &&
                          dependentIds?.length &&
                          !checkArraySubset(checkedIds, dependentIds)
                      ),
                      disabled: isDisabled,
                  },
        [checkboxPropsGetter, checkedIds, data, dependentIds, idKey, isDisabled],
    );

    const onCheckChangeLocal = useCallback(
        ({ checked }: { checked: boolean }) => {
            let newCheckedIds;

            if (dependentIds?.length) {
                const idsExcludeDependent = checkedIds?.filter((id) => !dependentIds.includes(id)) || [];

                newCheckedIds = checked ? [...idsExcludeDependent, ...dependentIds] : idsExcludeDependent;
            } else {
                const idsExcludeCurrent = checkedIds?.filter((id) => id !== data?.[idKey]) || [];

                newCheckedIds = checked ? [...(checkedIds || []), data?.[idKey]] : idsExcludeCurrent;
            }

            onCheckChange?.(newCheckedIds);
        },
        [checkedIds, data, dependentIds, idKey, onCheckChange],
    );

    return (noCheckboxKey && data[noCheckboxKey]) || noCheckboxChecker?.(data) ? (
        <div />
    ) : (
        <div style={style}>
            {message ? (
                <CheckboxWithToolTip
                    checkboxProps={checkboxProps}
                    tooltipProps={{
                        mode: 'mouseover',
                        content: message,
                    }}
                    fakeDisabled={isDisabled}
                />
            ) : (
                <Checkbox
                    intermediate={checkboxProps?.intermediate}
                    checked={checkboxProps?.checked}
                    onChange={onCheckChangeLocal}
                    disabled={checkboxProps?.disabled}
                />
            )}
        </div>
    );
};

AgCheckBoxCellRendererComponent.displayName = 'AgCheckBoxCellRendererComponent';
